import { useMemo } from "react";

import { skipToken } from "@reduxjs/toolkit/query";
import { useSelector } from "react-redux";

import { selectProfileSSOid } from "../../app-main/selectors/profile.selectors";
import { useGetIncompleteEventFaqSpacesQuery } from "../../services/faqs.service";
import type { RecommendedCardItem } from "../types/model/recommended";
import { toEventFaqItems } from "../utils/host-hub-event-faq.utils";

import useHostHubOwnerSpaces from "./useHostHubOwnerSpaces";

type UseHostHubIncompleteEventFaqSpacesResult = {
  isError: boolean;
  isLoading: boolean;
  spaces: RecommendedCardItem[];
};

/**
 * Fetches the host's spaces with an incomplete event FAQ and derives the
 * (capped) rows for the "Fill out your event FAQ" recommendation.
 *
 * 1. `GET /v2/spaces-v2/spaces/owner/faqs/event/incomplete-all` → incomplete space IDs
 * 2. `GET /v1/spaces/owner/:sso-id?full=true` → space titles and images
 */
const useHostHubIncompleteEventFaqSpaces =
  (): UseHostHubIncompleteEventFaqSpacesResult => {
    const ssoId = useSelector(selectProfileSSOid);

    const ownerSpaces = useHostHubOwnerSpaces();

    const incompleteEventFaqQuery = useGetIncompleteEventFaqSpacesQuery(
      ssoId ? undefined : skipToken,
    );

    const spaces = useMemo(
      () =>
        toEventFaqItems({
          incompleteSpaceIds: incompleteEventFaqQuery.data?.spaceIds,
          spaces: ownerSpaces.spaces,
        }),
      [ownerSpaces.spaces, incompleteEventFaqQuery.data],
    );

    const isLoading =
      ownerSpaces.isLoading || incompleteEventFaqQuery.isLoading;

    const isError = ownerSpaces.isError || incompleteEventFaqQuery.isError;

    return {
      isError,
      isLoading,
      spaces,
    };
  };

export default useHostHubIncompleteEventFaqSpaces;
